import {
  QueryConstraint,
  QuerySnapshot,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import {
  IGenericError,
  ISchool,
  ISchoolFulfilled,
  ISchoolRaw,
  ISchoolYearClass,
  ISchoolYearClassRaw,
  ISchoolYearFulfilled,
  ISchoolYearRaw,
} from '../types'
import { DocumentSnapshot } from 'firebase-admin/firestore'

const schoolsCollectionName = 'schools'
const schoolsYearsSubCollectionName = 'years'
const schoolsYearsClassesSubCollectionName = 'classes'

const schoolsCollectionRef = collection(firebaseDb, schoolsCollectionName)

const schoolsDocRef = (id: string) => doc(firebaseDb, schoolsCollectionName, id)

export async function getSchoolById(
  schoolId: string,
): Promise<ISchool | undefined> {
  try {
    const docRef = schoolsDocRef(schoolId)
    const docSnap = (await getDoc(
      docRef,
    )) as unknown as DocumentSnapshot<ISchoolRaw>

    const dacData = docSnap.data()

    if (!dacData) {
      return undefined
    }

    const doc: ISchool = {
      id: docSnap.id,
      ...dacData,
    }

    return doc
  } catch (error: unknown) {
    console.error(
      `error while tying to get school by id: ${schoolId} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get school by id`,
    )
  }
}

export async function getSchoolFulfilledWithSubCollectionsById(
  schoolId: string,
  teacherId?: string,
): Promise<ISchoolFulfilled | undefined> {
  try {
    const docRef = schoolsDocRef(schoolId)
    const docSnap = (await getDoc(
      docRef,
    )) as unknown as DocumentSnapshot<ISchoolRaw>

    const schoolDocData = docSnap.data()

    if (!schoolDocData) {
      return undefined
    }

    const school: ISchool = {
      id: docSnap.id,
      ...schoolDocData,
    }

    const years = await getSchoolYearsSubCollectionsById(school.id, teacherId)

    return { ...school, years }
  } catch (error: unknown) {
    console.error(
      `error while tying to get school fulfilled with sub-collections by id: ${schoolId} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get school fulfilled with sub-collections by id`,
    )
  }
}

export async function getSchoolYearsSubCollectionsById(
  schoolId: string,
  teacherId?: string,
): Promise<ISchoolYearFulfilled[]> {
  try {
    // get all years (sub-collection of school)
    const yearsRef = collection(
      firebaseDb,
      `${schoolsCollectionName}/${schoolId}/${schoolsYearsSubCollectionName}`,
    )

    const yearsSnap = (await getDocs(yearsRef)) as QuerySnapshot<ISchoolYearRaw>

    const years: ISchoolYearFulfilled[] = await Promise.all(
      yearsSnap.docs.map(async (yearDoc) => {
        const yearDocData = yearDoc.data()

        const classes = await getSchoolYearsClassesSubCollectionsById(
          schoolId,
          yearDoc.id,
          teacherId,
        )

        const year: ISchoolYearFulfilled = {
          id: yearDoc.id,
          ...yearDocData,
          classes,
        }

        return year
      }),
    )

    return years
  } catch (error: unknown) {
    console.error(
      `error while tying to get school years sub-collections by schoolId: ${schoolId} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get school years sub-collections by schoolId`,
    )
  }
}

export async function getSchoolYearsClassesSubCollectionsById(
  schoolId: string,
  yearId: string,
  teacherId?: string,
): Promise<ISchoolYearClass[]> {
  try {
    const classesRef = collection(
      firebaseDb,
      `${schoolsCollectionName}/${schoolId}/${schoolsYearsSubCollectionName}/${yearId}/${schoolsYearsClassesSubCollectionName}`,
    )

    let queryConstraints: QueryConstraint[] = []

    if (teacherId) {
      queryConstraints.push(
        where('classTeacherId', 'array-contains', teacherId),
      )
    }

    const q = query(classesRef, ...queryConstraints)

    const classesSnap = (await getDocs(q)) as QuerySnapshot<ISchoolYearClassRaw>

    const classes: ISchoolYearClass[] = []

    classesSnap.docs.forEach((classDoc) => {
      const yearClass: ISchoolYearClass = {
        id: classDoc.id,
        ...classDoc.data(),
      }

      classes.push(yearClass)
    })

    return classes
  } catch (error: unknown) {
    console.error(
      `error while tying to get school years classes sub-collections by schoolId: ${schoolId}, yearId: ${yearId} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get school years classes sub-collections by schoolId`,
    )
  }
}

export async function getSchoolYearsClassById(
  schoolId: string,
  yearId: string,
  classId: string,
): Promise<ISchoolYearClass | undefined> {
  try {
    const classDocRef = doc(
      firebaseDb,
      `${schoolsCollectionName}/${schoolId}/${schoolsYearsSubCollectionName}/${yearId}/${schoolsYearsClassesSubCollectionName}/${classId}`,
    )

    const classDocSnap = (await getDoc(
      classDocRef,
    )) as unknown as DocumentSnapshot<ISchoolYearClassRaw>

    const classDocData = classDocSnap.data()

    if (!classDocData) {
      return undefined
    }

    const classDoc: ISchoolYearClass = {
      id: classDocSnap.id,
      ...classDocData,
    }

    return classDoc
  } catch (error: unknown) {
    console.error(
      `error while tying to get school year class by id. schoolId: ${schoolId}, yearId: ${yearId}, classId: ${classId} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get school years classes sub-collections by schoolId`,
    )
  }
}

// Teacher sets what the class is working on right now. Writes only the
// `currentTopic` field on the class doc (plus updatedAt); pass an empty
// name to clear it.
export async function updateSchoolClassCurrentTopic(
  schoolId: string,
  yearId: string,
  classId: string,
  topicName: string,
  teacherId: string,
): Promise<void> {
  const name = topicName.trim()
  const classDocRef = doc(
    firebaseDb,
    schoolsCollectionName,
    schoolId,
    schoolsYearsSubCollectionName,
    yearId,
    schoolsYearsClassesSubCollectionName,
    classId,
  )

  try {
    await updateDoc(classDocRef, {
      currentTopic: name
        ? { name, setBy: teacherId, setAt: serverTimestamp() }
        : null,
      updatedAt: serverTimestamp(),
    })
  } catch (error: unknown) {
    console.error('[CLASS_TOPIC] update failed :>> ', {
      message: (error as IGenericError)?.message,
      error,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        'Unexpected error while saving the current topic',
    )
  }
}
